import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Package,
  Database,
  Workflow,
  Shield,
  FileSearch,
  Sparkles,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Card } from '@/components/ui/card';
import { EarlyAccessForm } from '@/components/EarlyAccessForm';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const WHAT_IT_DOES = [
  {
    icon: Package,
    title: 'Installed components inventory',
    detail:
      'Catalogs every component installed in your WebCenter Content environment — Oracle stock components, third-party components, custom components. Versions, dependencies, last-modified dates.',
  },
  {
    icon: Workflow,
    title: 'Workflow definitions analysis',
    detail:
      'Inventories every workflow definition, identifies dead workflows, surfaces approval routing complexity, and flags workflows likely to be hard to translate forward in a modernization.',
  },
  {
    icon: Database,
    title: 'Storage and archive analysis',
    detail:
      'Storage utilization across file system, database, and Object Storage. Document growth rate by content type. Decommissioning candidates. Managed Attachments link health.',
  },
  {
    icon: Shield,
    title: 'Security model snapshot',
    detail:
      'Custom security groups, roles, account hierarchy, and access pattern analysis. Critical for SOX / HIPAA / FedRAMP audit cycles and for modernization planning.',
  },
  {
    icon: FileSearch,
    title: 'Customization detection',
    detail:
      'Surfaces ADF customizations, custom content profiles, custom services, custom validation rules, and custom search results templates — the layers that complicate modernization.',
  },
  {
    icon: Sparkles,
    title: 'December 2026 decision mapping',
    detail:
      'Maps each finding to "what this means for your December 2026 decision." Personalized Path A / A+ / B / B+ recommendation based on your actual stack, not generic advice.',
  },
];

const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'Request early access',
    body: 'Sign up below. Andrew personally emails you within 1 business day to confirm fit and walk you through the installation.',
  },
  {
    step: '2',
    title: 'Install via Component Manager',
    body: 'Standard Oracle WebCenter Content component install — 5 minutes via the Component Manager admin UI. No vendor environment access. Read-only data collection. Component runs locally inside your WCC.',
  },
  {
    step: '3',
    title: 'Run the inspector',
    body: 'A new "Modernization Inspector" menu appears under WCC Admin Tools. Click to run. The inspector reads your environment configuration and produces a structured report — typically 5-15 minutes runtime depending on environment size.',
  },
  {
    step: '4',
    title: 'Export the report',
    body: 'The output is a clean PDF report shareable internally with your CIO, CFO, and Oracle account team. Includes the Path A / A+ / B / B+ recommendation and stack-specific findings.',
  },
];

const FAQS = [
  {
    q: 'Is this a real Oracle-supported WebCenter Content component?',
    a: 'Yes. The Inspector is a native WCC component installed via Component Manager — the standard Oracle-blessed extension mechanism. It runs inside your WCC environment using documented WCC services. No base-table writes, no vendor cloud callouts, no environment access for EZ Cloud.',
  },
  {
    q: 'What does it cost?',
    a: 'Free for the duration of the December 2026 decision window. The Inspector is part of EZ Cloud\'s WebCenter modernization outreach. No license fee, no per-user cost, no expiry.',
  },
  {
    q: 'Why early access? Why not just publish it?',
    a: 'The Inspector is intentionally early-access during the December 2026 decision window. First customers get founder-led installation and the chance to shape the Inspector\'s output to their decision needs. Once the early-access cohort is established, broader release follows.',
  },
  {
    q: 'What WCC versions does the Inspector support?',
    a: 'WebCenter Content 11g, 12c, and 14c. The Inspector detects the installed version and adapts data collection accordingly.',
  },
  {
    q: 'Does the Inspector send any data back to EZ Cloud?',
    a: 'No. The Inspector runs entirely inside your WCC environment and produces a local PDF report. There is no phone-home, no telemetry, no callout to ezcloud.ai. What happens in your environment stays in your environment.',
  },
  {
    q: 'Who built this?',
    a: `${FOUNDER.name}, founder of EZ Cloud and a 25-year Oracle WebCenter AP specialist. EZ Cloud is published on the Oracle Reference Architecture for OCI and featured in Oracle's own technical case studies. The Inspector reflects 25 years of WebCenter customization, modernization, and architecture experience.`,
  },
];

const WebCenterContentInspector = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="WebCenter AP Modernization Inspector — Free WCC Component | EZ Cloud"
        description="A free native Oracle WebCenter Content component that produces a December 2026 modernization-readiness report for your WCC environment. Installed via Component Manager. Runs locally — no vendor cloud callouts. Early access for selected WebCenter customers."
        canonical="https://ezcloud.ai/resources/webcenter-content-inspector"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Resources', url: 'https://ezcloud.ai/resources/webcenter-content-inspector' },
          {
            name: 'WCC Modernization Inspector',
            url: 'https://ezcloud.ai/resources/webcenter-content-inspector',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Sparkles className="w-4 h-4" aria-hidden />
              Free WCC Component · Early Access
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              The WebCenter AP
              <span className="block text-accent mt-3">Modernization Inspector</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              A free native Oracle WebCenter Content component that produces a personalized
              modernization-readiness report for your WCC environment — directly aligned to the
              December 2026 Fusion Middleware 12c support decision.
            </p>
            <p className="text-lg text-white/70 max-w-3xl">
              Installs via standard Component Manager. Runs locally inside your WCC. No vendor
              cloud callouts, no phone-home, no data leaves your environment. Just a clean PDF
              report you keep.
            </p>
          </div>
        </section>

        {/* Early-access form */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-3xl">
            <EarlyAccessForm
              tool="WCC Modernization Inspector"
              source="inspector-top"
              eventName="webcenter_inspector_early_access"
              buttonLabel="Request early access"
            />
          </div>
        </section>

        {/* What it does */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What the Inspector does
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Six dimensions of your WCC environment, analyzed against the December 2026 decision
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The Inspector is purpose-built for WCC customers facing the Fusion Middleware 12c
              support cliff. Every output dimension maps to a decision you need to make.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {WHAT_IT_DOES.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <h3 className="text-gray-900 font-semibold mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How it works
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              From early access to PDF report in under an hour
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The Inspector uses Oracle's standard Component Manager install pattern — the same
              mechanism your team already uses for any WCC component.
            </p>
            <div className="space-y-5">
              {HOW_IT_WORKS.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why this matters */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <Card className="p-8 md:p-10 bg-white border border-gray-200 shadow-md">
              <p className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                Why we built this
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Every WebCenter Content customer has a December 2026 decision to make
              </h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Oracle Fusion Middleware 12c Premier Support ends December 2026. Every WCC
                  customer is going to be asked the same questions by their CIO, CFO, and Oracle
                  account team in the next 6 months: "What do we actually have running? What
                  changes? What's our forward path?"
                </p>
                <p>
                  Most WCC environments have grown sophisticated over a decade. Documentation grew
                  sparse. The original implementers may no longer be available. Answering those questions properly
                  requires either a long internal discovery effort, a paid consulting engagement,
                  or the right tooling.
                </p>
                <p>
                  The Inspector is the tooling. Five-minute install. Five-to-fifteen-minute
                  runtime. PDF report you keep. Useful regardless of which path you end up choosing
                  — Path A, A+, B, or B+.
                </p>
                <p>
                  <Link
                    to="/resources/webcenter-decision-guide"
                    className="font-semibold text-primary hover:text-accent transition-colors"
                  >
                    Read the December 2026 Decision Guide
                  </Link>{' '}
                  for the framework the Inspector's output is built against.
                </p>
              </div>
            </Card>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers about the Inspector
            </h2>
            <div className="space-y-5">
              {FAQS.map((f) => (
                <Card key={f.q} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{f.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{f.a}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom early-access form */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              Ready to inspect your WCC environment?
            </h2>
            <p className="text-xl text-white/80 text-center mb-10">
              Request early access. {FOUNDER.name} will personally email you within 1 business day.
            </p>
            <EarlyAccessForm
              tool="WCC Modernization Inspector"
              source="inspector-bottom"
              eventName="webcenter_inspector_early_access"
              buttonLabel="Request early access"
            />
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterContentInspector;
